import React, { createContext, useEffect, useState } from 'react';
import RaisedButton from 'material-ui/RaisedButton';
import { Link } from 'react-router';
import KeyboardArrowLeftIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-left';
import DoneIcon from 'material-ui/svg-icons/action/done';
import HighlightOffIcon from 'material-ui/svg-icons/action/highlight-off';
import Sidebar from './Sidebar';
import ErrorBoundary from '../../common/ErrorBoundary';
import MyMapComponent from '../PortAdd/MapComponent';
import Facilities from './blocks/Facilities/Facilities';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { getPort, clearPort } from 'actions/regions/ports';
import constants from 'constants';
import { formName } from '../../../data/formNames';
import { reduxForm } from 'redux-form';
import _ from 'lodash';
import PortRestrictions from './blocks/PortRestrictions/PortRestrictions';
import Warehouses from './blocks/Warehouses/Warehouses';
import Other from './blocks/Other/Other';
import { getCountries } from 'actions/countries';
import LiftAndCranes from './blocks/LiftAndCranes/LiftAndCranes';
import ServiceProviders from './blocks/ServiceProviders/ServiceProviders';
import { usePortMutations, useServiceProvidersMutations } from './hooks/ports';

const styles = {
    btnBack: {
        labelStyle: { color: '#fff', textTransform: 'none', fontSize: '13px' },
        style: { margin: '20px 0 70px 0' },
    },
    btnSubmit: {
        labelStyle: { color: '#fff', textTransform: 'none', fontSize: '13px' },
        style: { margin: '20px 0 70px 0' },
    },
};

const serializeId = v => (v && typeof v === 'object' && v._id ? v._id : v || undefined);

export const ContextCountries = createContext([]);

const PortManagement = ({
    port: initialPort, getPort, clearPort, routeParams, router, getCountries, countries,
}) => {
    const [state, setState] = useState({
        port: initialPort,
        addedBy: { email: '', name: '', addedAt: '' },
        newPort: initialPort,
        serviceProviders: initialPort.serviceProviders,
        newServiceProviders: initialPort.serviceProviders,
        disabled: false,
        liftsCranes: initialPort.liftsCranes,
    });

    const {
        approve, reject, updateField, approveField, rejectField, 
    } = usePortMutations(initialPort._id);

    const {
        approveServiceProvider, 
        rejectServiceProvider, 
        updateServiceProvider,
        approveNewServiceProvider,
        rejectNewServiceProvider,
        approveRemoveServiceProvider,
        rejectRemoveServiceProvider,
    } = useServiceProvidersMutations(initialPort._id);

    const handleApproveNewServiceProvider = (serviceProvider, onComplete) => {
        approveNewServiceProvider.mutate(
            {
                _id: state.port._id,
                historyId: state.port.history[0]._id,
                updateAt: Date.parse(state.port.history[0].updateAt),
                providerId: serviceProvider._id,
            },
            {
                onSuccess: () => {
                    clearPort();
                    if (onComplete) {
                        onComplete();
                    }
                    return getPort({ id: routeParams.id, history: true, all: true });
                },
            },
        );
    };
    
    const handleRejectNewServiceProvider = (serviceProvider, onComplete) => {
        rejectNewServiceProvider.mutate(
            {
                _id: state.port._id,
                historyId: state.port.history[0]._id,
                updateAt: Date.parse(state.port.history[0].updateAt),
                providerId: serviceProvider._id,
            },
            {
                onSuccess: () => {
                    clearPort();
                    if (onComplete) {
                        onComplete();
                    }
                    return getPort({ id: routeParams.id, history: true, all: true });
                },
            },
        );
    };  

    const handleApproveRemoveServiceProvider = (serviceProvider) => {
        approveRemoveServiceProvider.mutate(
            {
                _id: state.port._id,
                historyId: state.port.history[0]._id,
                updateAt: Date.parse(state.port.history[0].updateAt),
                providerId: serviceProvider._id,
            },
            {
                onSuccess: () => {
                    clearPort();
                    return getPort({ id: routeParams.id, history: true, all: true });
                },
            },
        );
    };

    const handleRejectRemoveServiceProvider = (serviceProvider) => {
        rejectRemoveServiceProvider.mutate(
            {
                _id: state.port._id,
                historyId: state.port.history[0]._id,
                updateAt: Date.parse(state.port.history[0].updateAt),
                providerId: serviceProvider._id,
            },
            {
                onSuccess: () => {
                    clearPort();
                    return getPort({ id: routeParams.id, history: true, all: true });
                },
            },
        );
    };

    const handleApproveServiceProvider = (serviceProvider, opts = { isNew: false, isRemove: false }) => {
        if (opts.isNew) {
            return handleApproveNewServiceProvider(serviceProvider, opts.onComplete);
        }

        if (opts.isRemove) {
            return handleApproveRemoveServiceProvider(serviceProvider);
        }

        approveServiceProvider.mutate(
            {
                _id: serviceProvider._id,
                historyId: serviceProvider.history._id,
                updateAt: Date.parse(serviceProvider.history.updateAt),
            },
            {
                onSuccess: () => {
                    clearPort();
                    return getPort({ id: routeParams.id, history: true, all: true });
                },
            },
        );
    };

    const handleRejectServiceProvider = (serviceProvider, opts = { isNew: false, isRemove: false }) => {
        if (opts.isNew) {
            return handleRejectNewServiceProvider(serviceProvider, opts.onComplete);
        }

        if (opts.isRemove) {
            return handleRejectRemoveServiceProvider(serviceProvider);
        }

        console.log('handleRejectServiceProvider', serviceProvider);

        rejectServiceProvider.mutate(
            {
                _id: serviceProvider._id,
                historyId: serviceProvider.history._id,
                updateAt: Date.parse(serviceProvider.history.updateAt),
            },
            {
                onSuccess: () => {
                    clearPort();
                    return getPort({ id: routeParams.id, history: true, all: true });
                },
            },
        );
    };

    const handleUpdateServiceProvider = (serviceProviders, onComplete) => {
        const payloadProviders = Array.isArray(serviceProviders)
            ? serviceProviders.map((sp) => {
                if (!sp.recommended || !sp.recommended.endAt) {
                    delete sp.recommended;
                }

                if (!sp.contactPersonId || !sp.contactPersonId._id) {
                    delete sp.contactPersonId;
                }
                
                return {
                    ...sp,
                    companyId: serializeId(sp.companyId),
                    contactPersonId: serializeId(sp.contactPersonId),
                };
            })
            : serviceProviders;
        
        updateServiceProvider.mutate(
            {
                _id: state.port._id,
                historyId: state.port.history[0]._id,
                updateAt: Date.parse(state.port.history[0].updateAt),
                serviceProviders: payloadProviders,
            },
            {
                onSuccess: () => {
                    clearPort();
                    if (onComplete) {
                        onComplete();
                    }
                    return getPort({ id: routeParams.id, history: true, all: true });
                },
            },
        );
    };
    
    /**
     * @param {string} field
     */
    const handleApproveField = (field) => {
        approveField.mutate(
            {
                _id: state.port._id,
                historyId: state.port.history[0]._id,
                updateAt: Date.parse(state.port.history[0].updateAt),
                fields: [field],
            }, 
            {
                onSuccess: () => {
                    clearPort();
                    return getPort({ id: routeParams.id, history: true, all: true });
                },
            },
        );
    };

    /**
     * @param {string} field
     */
    const handleRejectField = (field) => {
        rejectField.mutate(
            {
                _id: state.port._id,
                historyId: state.port.history[0]._id,
                updateAt: Date.parse(state.port.history[0].updateAt),
                fields: [field],
            }, 
            {
                onSuccess: () => {
                    clearPort();
                    return getPort({ id: routeParams.id, history: true, all: true });
                },
            },
        );
    };

    /**
     * @param {string} field
     * @param {any} value
     * @param {function} [onComplete]
     */
    const updatePortField = (field, value, onComplete) => {
        updateField.mutate(
            {
                _id: state.port._id,
                historyId: state.port.history[0]._id,
                updateAt: Date.parse(state.port.history[0].updateAt),
                toUpdate: { field, value },
            },
            {
                onSuccess: () => {
                    clearPort();
                    getPort({ id: routeParams.id, history: true, all: true });
                    if (onComplete) {
                        onComplete();
                    }
                },
            },
        );
    };

    useEffect(() => {
        getPort({ id: routeParams.id, history: true, all: true });
        getCountries();
        
        return () => clearPort();
    }, []);

    useEffect(() => {
        const newState = {
            port: initialPort,
            newPort: initialPort,
            disabled: false,
            addedBy: state.addedBy,
            serviceProviders: initialPort.serviceProviders,
            newServiceProviders: initialPort.serviceProviders,
            liftsCranes: initialPort.liftsCranes,
            newLiftsCranes: initialPort.liftsCranes,
        };

        if (initialPort.firstLoad && initialPort.history && initialPort.history.length === 0) {
            clearPort();
            router.push(`/regions/ports/${routeParams.id}`);
            return;
        }

        if (initialPort.history && initialPort.history.length > 0) {
            newState.port = Object.assign({}, initialPort, initialPort.history[0].originalValues);
            newState.newPort = Object.assign({}, initialPort, initialPort.history[0].changedValues);

            if (!newState.port.portLimitations) {
                newState.port.portLimitations = initialPort.portLimitations;
            }

            if (!initialPort.history[0].changedValues.portLimitations) {
                delete newState.newPort.portLimitations;
            }

            newState.addedBy = initialPort.history[0].addedBy;
            newState.addedBy.addedAt = initialPort.history[0].addedAt;

            newState.serviceProviders = [...initialPort.history[0].originalValues.serviceProviders];
            newState.newServiceProviders = [...initialPort.history[0].changedValues.serviceProviders];

            if (initialPort.history[0].originalValues.liftsCranes) {
                newState.liftsCranes = { ...initialPort.history[0].originalValues.liftsCranes };
            }

            newState.newLiftsCranes = { ...initialPort.history[0].changedValues.liftsCranes };

            // Process service providers
            newState.serviceProviders.forEach((serviceProvider) => {
                const currentServiceProvider = newState.newServiceProviders.find(newServiceProvider => serviceProvider._id === newServiceProvider._id);

                if (currentServiceProvider && currentServiceProvider.history.length && _.isEqual(
                    _.omit(currentServiceProvider, ['_id', 'history', '__v']),
                    currentServiceProvider.history[0].changedValues,
                )) {
                    serviceProvider.status = 'normal';
                    return;
                }

                if (currentServiceProvider && currentServiceProvider.history.length !== 0) {
                    serviceProvider.status = 'change';
                    serviceProvider.provider = currentServiceProvider.history[0].changedValues;
                    serviceProvider.history = currentServiceProvider.history[0];
                    newState.disabled = true;
                } else if (currentServiceProvider && currentServiceProvider.history.length === 0) {
                    serviceProvider.status = 'normal';
                } else {
                    serviceProvider.status = 'remove';
                    // serviceProvider.history = currentServiceProvider.history[0];
                }
            });

            // Process new service providers
            newState.newServiceProviders.forEach((newServiceProvider) => {
                const currentServiceProvider = newState.serviceProviders.find(serviceProvider => serviceProvider._id === newServiceProvider._id);

                if (!currentServiceProvider) {
                    newState.serviceProviders.push(newServiceProvider);
                    newState.serviceProviders[newState.serviceProviders.length - 1].status = 'new';
                }
            });

            newState.serviceProviders = [...newState.serviceProviders, ...initialPort.serviceProviders];
            newState.serviceProviders = _.uniqBy(newState.serviceProviders, '_id');

            newState.serviceProviders.forEach((serviceProvider) => {
                if (!serviceProvider.status) {
                    serviceProvider.status = 'normal';
                }
            });

            // Process lifts and cranes
            Object.keys(newState.liftsCranes).forEach((key) => {
                newState.liftsCranes[key].details.forEach((detail) => {
                    const currentDetail = newState.newLiftsCranes[key].details.find(newDetail => detail._id === newDetail._id);

                    if (currentDetail && (detail.value !== currentDetail.value || detail.name !== currentDetail.name)) {
                        detail.status = 'change';
                        detail.changes = currentDetail;
                    } else if (currentDetail) {
                        detail.status = 'normal';
                        // detail.changes = currentDetail;
                    } else {
                        detail.status = 'remove';
                    }
                });
            });

            Object.keys(newState.newLiftsCranes).forEach((key) => {
                newState.newLiftsCranes[key].details.forEach((newDetail) => {
                    const currentDetail = newState.liftsCranes[key] && 
                        newState.liftsCranes[key].details.find(detail => detail._id === newDetail._id);

                    if (!currentDetail) {
                        newState.liftsCranes[key].details.push(newDetail);
                        newState.liftsCranes[key].details[newState.liftsCranes[key].details.length - 1].status = 'new';
                    }
                });
            });
        }

        setState(newState);
    }, [initialPort]);

    const approvePort = async () => {
        approve.mutate(
            {
                _id: state.port._id,
                historyId: state.port.history[0]._id,
                updateAt: Date.parse(state.port.history[0].updateAt),
            }, 
            {
                onSuccess: () => {
                    clearPort();
                    return getPort({ id: routeParams.id, history: true, all: true });
                },
            },
        );
    };

    const rejectPort = async () => {
        reject.mutate(
            {
                _id: state.port._id,
                historyId: state.port.history[0]._id,
                updateAt: Date.parse(state.port.history[0].updateAt),
            }, 
            {
                onSuccess: () => {
                    clearPort();
                    return getPort({ id: routeParams.id, history: true, all: true });
                },
                onError: (error) => {
                    console.error('Failed to reject port:', error);
                },
            },
        );
    };
    
    const {
        port, newPort, addedBy, disabled, serviceProviders,
    } = state;

    return (
        <div className='container' id='single-port'>
            <div className='management'>
                <div className='btn'>
                    <RaisedButton
                        label="Back to list"
                        style={styles.btnBack.style}
                        labelStyle={styles.btnBack.labelStyle}
                        labelPosition="before"
                        icon={<KeyboardArrowLeftIcon />}
                        containerElement={<Link to='/regions/ports' />}
                        primary
                    />
                </div>
                <div className='btn'>
                    <RaisedButton
                        label='Approve port modification'
                        style={styles.btnSubmit.style}
                        labelStyle={styles.btnSubmit.labelStyle}
                        labelPosition='before'
                        icon={<DoneIcon />}
                        backgroundColor='#a4c639'
                        onClick={approvePort}
                        disabled={disabled}
                    />
                </div>
                <div className='btn'>
                    <RaisedButton
                        label='Reject port modification'
                        style={styles.btnSubmit.style}
                        labelStyle={styles.btnSubmit.labelStyle}
                        labelPosition='before'
                        icon={<HighlightOffIcon />}
                        backgroundColor='#f00'
                        onClick={rejectPort}
                        disabled={disabled}
                    />
                </div>
            </div>

            {
                addedBy.email ?
                    <div className='management'>
                        <div className='paper-block'>
                            <span className='name-block'>Added by</span>
                            <div>Email - {addedBy.email}</div>
                            <div>Name - {addedBy.name}</div>
                            <div>Added at - {new Date(addedBy.addedAt).toLocaleString('en-GB', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                            </div>
                        </div>
                    </div> : null
            }

            <div className='row'>
                <ErrorBoundary>
                    <Sidebar
                        port={port}
                        newPort={newPort}
                    />
                </ErrorBoundary>
                <ContextCountries.Provider value={countries}>
                    <div className='col-lg-9 col-xs-9 main'>
                        <ErrorBoundary>
                            <div className='add-coords-map'>
                                <div className='main__title'>Coordinates</div>
                                <MyMapComponent
                                    coordinates={port.coordinates}
                                    googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${constants.GOOGLE_MAP_API_KEY}`}
                                    loadingElement={<div style={{ height: '100%' }} />}
                                    containerElement={<div style={{ width: '100%', height: '300px' }} />}
                                    mapElement={<div style={{ height: '100%' }} />}
                                    rememberOldCoords
                                />
                            </div>
                        </ErrorBoundary>
                        <ErrorBoundary>
                            <ServiceProviders
                                port={state.newPort}
                                pendingChanges={port.history ? port.history[0] : null}
                                serviceProviders={serviceProviders}
                                onSaveChanges={handleUpdateServiceProvider}
                                onApproveField={handleApproveServiceProvider}
                                onRejectField={handleRejectServiceProvider}
                            />
                        </ErrorBoundary>
                        <ErrorBoundary>
                            <PortRestrictions
                                port={state.newPort}
                                pendingChanges={port.history ? port.history[0] : null}
                                onApproveField={handleApproveField}
                                onRejectField={handleRejectField}
                                updatePortField={updatePortField}
                            />
                        </ErrorBoundary>
                        <ErrorBoundary>
                            <LiftAndCranes 
                                port={state.newPort}
                                pendingChanges={port.history ? port.history[0] : null}
                                onApproveField={handleApproveField}
                                onRejectField={handleRejectField}
                                updatePortField={updatePortField}
                            />
                        </ErrorBoundary>
                        <ErrorBoundary>
                            <Facilities
                                port={state.newPort}
                                pendingChanges={port.history ? port.history[0] : null}
                                updatePortField={updatePortField}
                                onApproveField={handleApproveField}
                                onRejectField={handleRejectField}
                            />
                        </ErrorBoundary>
                        <ErrorBoundary>
                            <Warehouses
                                port={state.newPort}
                                pendingChanges={port.history ? port.history[0] : null}
                                updatePortField={updatePortField}
                                onApproveField={handleApproveField}
                                onRejectField={handleRejectField}
                            />
                        </ErrorBoundary>
                        <ErrorBoundary>
                            <Other
                                port={state.newPort}
                                pendingChanges={port.history ? port.history[0] : null}
                                updatePortField={updatePortField}
                                onApproveField={handleApproveField}
                                onRejectField={handleRejectField}
                            />
                        </ErrorBoundary>
                    </div>
                </ContextCountries.Provider>
            </div>
        </div>
    );
};

const PortManagementForm = reduxForm({
    form: formName.portUpdate,
    enableReinitialize: true,
})(PortManagement);

export default connect(
    ({ port, user: { isLogged }, countries }) => ({
        port,
        isLogged,
        countries: countries && countries.items,
    }),
    dispatch => bindActionCreators({
        getPort,
        clearPort,
        getCountries,
    }, dispatch),
)(PortManagementForm);




// WEBPACK FOOTER //
// ./app/components/Regions/PortManagement/PortManagement.jsx